import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getRemediationTasks,
  getRemediationTaskStats,
  createRemediationTask,
  updateRemediationTask,
  deleteRemediationTask,
  exportRemediationTasksCsv,
  exportRemediationTasksMarkdown,
  importFindingsToBacklog,
  deleteAllTasksForAudit,
  bulkDeleteTasks,
  RemediationTaskListItem,
  RemediationTaskCreate,
  RemediationTaskUpdate,
  TaskImpact,
  TaskPriority,
  TaskStatus,
  TaskComplexity,
  TimeEstimate,
} from '../services/api'

const glassPanel = { background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(100, 180, 255, 0.1)' }

// Style mappings
const impactColors: Record<TaskImpact, string> = {
  critical: '#dc2626',
  high: '#f87171',
  medium: '#fbbf24',
  low: '#22c55e',
  info: '#60a5fa',
}

const priorityColors: Record<TaskPriority, string> = {
  P1: '#dc2626',
  P2: '#f87171',
  P3: '#fbbf24',
  P4: '#22c55e',
  P5: '#94a3b8',
}

const statusColors: Record<TaskStatus, string> = {
  backlog: '#94a3b8',
  todo: '#60a5fa',
  in_progress: '#fbbf24',
  blocked: '#f87171',
  in_review: '#a78bfa',
  completed: '#22c55e',
  deferred: '#6b7280',
  wont_fix: '#374151',
}

const statusLabels: Record<TaskStatus, string> = {
  backlog: 'Backlog',
  todo: 'To Do',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  in_review: 'In Review',
  completed: 'Completed',
  deferred: 'Deferred',
  wont_fix: "Won't Fix",
}

interface BacklogPanelProps {
  projectId: string
  auditId: string
  searchTerm?: string
  presentationMode?: boolean
}

export default function BacklogPanel({ projectId, auditId, searchTerm, presentationMode }: BacklogPanelProps) {
  const queryClient = useQueryClient()
  const [sortBy, setSortBy] = useState('risk_score')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [filterStatus, setFilterStatus] = useState<TaskStatus | 'all'>('all')
  const [filterPriority, setFilterPriority] = useState<TaskPriority | 'all'>('all')
  const [selectedTask, setSelectedTask] = useState<RemediationTaskListItem | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set())
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteResult, setDeleteResult] = useState<{ deleted: number } | null>(null)

  // Fetch tasks
  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ['remediation-tasks', projectId, auditId, sortBy, sortOrder, filterStatus, filterPriority, searchTerm],
    queryFn: async () => {
      const response = await getRemediationTasks({
        project_id: projectId,
        audit_id: auditId,
        status: filterStatus === 'all' ? undefined : filterStatus,
        priority: filterPriority === 'all' ? undefined : filterPriority,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: 500,
      })
      return response.data
    },
  })

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['remediation-task-stats', projectId, auditId],
    queryFn: async () => {
      const response = await getRemediationTaskStats({ project_id: projectId, audit_id: auditId })
      return response.data
    },
  })

  // Update task mutation
  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId, update }: { taskId: string; update: RemediationTaskUpdate }) => {
      const response = await updateRemediationTask(taskId, update)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['remediation-tasks'] })
      queryClient.invalidateQueries({ queryKey: ['remediation-task-stats'] })
    },
  })

  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      await deleteRemediationTask(taskId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['remediation-tasks'] })
      queryClient.invalidateQueries({ queryKey: ['remediation-task-stats'] })
      setSelectedTask(null)
    },
  })

  // Export handlers
  const handleExportCsv = async () => {
    setIsExporting(true)
    try {
      const response = await exportRemediationTasksCsv({
        project_id: projectId,
        audit_id: auditId,
        status: filterStatus === 'all' ? undefined : filterStatus,
      })
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `remediation-tasks-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
    }
    setIsExporting(false)
  }

  const handleExportMarkdown = async (includeDetails: boolean) => {
    setIsExporting(true)
    try {
      const response = await exportRemediationTasksMarkdown({
        project_id: projectId,
        audit_id: auditId,
        status: filterStatus === 'all' ? undefined : filterStatus,
        include_details: includeDetails,
      })
      const blob = new Blob([response.data], { type: 'text/markdown' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `remediation-tasks-${new Date().toISOString().split('T')[0]}.md`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
    }
    setIsExporting(false)
  }

  const handleImportFindings = async () => {
    setIsImporting(true)
    setImportResult(null)
    setImportError(null)
    try {
      const response = await importFindingsToBacklog(auditId, 'Low')
      setImportResult({ imported: response.data.imported, skipped: response.data.skipped })
      // Refresh task list and stats
      queryClient.invalidateQueries({ queryKey: ['remediation-tasks'] })
      queryClient.invalidateQueries({ queryKey: ['remediation-task-stats'] })
    } catch (error: unknown) {
      console.error('Import failed:', error)
      const err = error as { response?: { data?: { detail?: string } }; message?: string }
      setImportError(err.response?.data?.detail || err.message || 'Import failed')
    }
    setIsImporting(false)
  }

  const handleSelectAll = () => {
    if (selectedTasks.size === tasks.length) {
      setSelectedTasks(new Set())
    } else {
      setSelectedTasks(new Set(tasks.map(t => t.id)))
    }
  }

  const handleSelectTask = (taskId: string) => {
    const newSelected = new Set(selectedTasks)
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId)
    } else {
      newSelected.add(taskId)
    }
    setSelectedTasks(newSelected)
  }

  const handleBulkDelete = async () => {
    if (selectedTasks.size === 0) return
    if (!confirm(`Delete ${selectedTasks.size} selected task(s)? This cannot be undone.`)) return

    setIsDeleting(true)
    setDeleteResult(null)
    try {
      const response = await bulkDeleteTasks(Array.from(selectedTasks))
      setDeleteResult({ deleted: response.data.deleted })
      setSelectedTasks(new Set())
      queryClient.invalidateQueries({ queryKey: ['remediation-tasks'] })
      queryClient.invalidateQueries({ queryKey: ['remediation-task-stats'] })
    } catch (error) {
      console.error('Bulk delete failed:', error)
    }
    setIsDeleting(false)
  }

  const handleDeleteAll = async () => {
    if (!confirm(`Delete ALL ${tasks.length} tasks for this audit? This cannot be undone.`)) return

    setIsDeleting(true)
    setDeleteResult(null)
    try {
      const response = await deleteAllTasksForAudit(auditId)
      setDeleteResult({ deleted: response.data.deleted })
      setSelectedTasks(new Set())
      queryClient.invalidateQueries({ queryKey: ['remediation-tasks'] })
      queryClient.invalidateQueries({ queryKey: ['remediation-task-stats'] })
    } catch (error) {
      console.error('Delete all failed:', error)
    }
    setIsDeleting(false)
  }

  const handleStatusChange = (taskId: string, newStatus: TaskStatus) => {
    updateTaskMutation.mutate({ taskId, update: { status: newStatus } })
  }

  const handlePriorityChange = (taskId: string, newPriority: TaskPriority) => {
    updateTaskMutation.mutate({ taskId, update: { priority: newPriority } })
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="rounded-xl p-4 text-center" style={glassPanel}>
            <div className="text-3xl font-bold" style={{ color: '#60a5fa' }}>{stats.total}</div>
            <div className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Total Tasks</div>
          </div>
          <div className="rounded-xl p-4 text-center" style={glassPanel}>
            <div className="text-3xl font-bold" style={{ color: '#22c55e' }}>{stats.completion_rate.toFixed(0)}%</div>
            <div className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Completed</div>
          </div>
          <div className="rounded-xl p-4 text-center" style={glassPanel}>
            <div className="text-3xl font-bold" style={{ color: '#dc2626' }}>{stats.by_priority?.P1 || 0}</div>
            <div className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>P1 Critical</div>
          </div>
          <div className="rounded-xl p-4 text-center" style={glassPanel}>
            <div className="text-3xl font-bold" style={{ color: '#f87171' }}>{stats.by_impact?.critical || 0}</div>
            <div className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Critical Impact</div>
          </div>
          <div className="rounded-xl p-4 text-center" style={glassPanel}>
            <div className="text-3xl font-bold" style={{ color: '#fbbf24' }}>{stats.by_status?.in_progress || 0}</div>
            <div className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>In Progress</div>
          </div>
          <div className="rounded-xl p-4 text-center" style={glassPanel}>
            <div className="text-3xl font-bold" style={{ color: '#a78bfa' }}>{stats.avg_risk_score?.toFixed(0) || '-'}</div>
            <div className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Avg Risk Score</div>
          </div>
        </div>
      )}

      {/* Controls Bar */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex flex-wrap items-center gap-4">
          {/* Filters */}
          <div className="flex items-center gap-2">
            <label className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as TaskStatus | 'all')}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
            >
              <option value="all">All</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Priority:</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value as TaskPriority | 'all')}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
            >
              <option value="all">All</option>
              <option value="P1">P1 - Critical</option>
              <option value="P2">P2 - High</option>
              <option value="P3">P3 - Medium</option>
              <option value="P4">P4 - Low</option>
              <option value="P5">P5 - Minimal</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Sort:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
            >
              <option value="risk_score">Risk Score</option>
              <option value="priority">Priority</option>
              <option value="impact">Impact</option>
              <option value="created_at">Created</option>
              <option value="due_date">Due Date</option>
            </select>
            <button
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
            >
              <i className={`fa-solid fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
            </button>
          </div>

          {/* Import & Export Buttons */}
          {!presentationMode && (
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={handleImportFindings}
              disabled={isImporting}
              className="px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 disabled:opacity-50"
              style={{ background: 'rgba(251, 146, 60, 0.2)', border: '1px solid rgba(251, 146, 60, 0.3)', color: '#fb923c' }}
            >
              {isImporting ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : (
                <i className="fa-solid fa-file-import"></i>
              )}
              Import All Findings
            </button>
            <div className="w-px h-6" style={{ background: 'rgba(100, 180, 255, 0.2)' }}></div>
            <button
              onClick={handleExportCsv}
              disabled={isExporting || tasks.length === 0}
              className="px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 disabled:opacity-50"
              style={{ background: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.3)', color: '#22c55e' }}
            >
              <i className="fa-solid fa-file-csv"></i>
              Export CSV
            </button>
            <button
              onClick={() => handleExportMarkdown(false)}
              disabled={isExporting || tasks.length === 0}
              className="px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 disabled:opacity-50"
              style={{ background: 'rgba(96, 165, 250, 0.2)', border: '1px solid rgba(96, 165, 250, 0.3)', color: '#60a5fa' }}
            >
              <i className="fa-brands fa-markdown"></i>
              Export MD
            </button>
            <button
              onClick={() => handleExportMarkdown(true)}
              disabled={isExporting || tasks.length === 0}
              className="px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 disabled:opacity-50"
              style={{ background: 'rgba(167, 139, 250, 0.2)', border: '1px solid rgba(167, 139, 250, 0.3)', color: '#a78bfa' }}
            >
              <i className="fa-solid fa-file-lines"></i>
              Full Report
            </button>
          </div>
          )}
        </div>
      </div>

      {/* Import Result Banner */}
      {importResult && (
        <div
          className="rounded-xl p-4 flex items-center justify-between"
          style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}
        >
          <div className="flex items-center gap-3">
            <i className="fa-solid fa-check-circle text-xl" style={{ color: '#22c55e' }}></i>
            <div>
              <p className="font-medium" style={{ color: '#22c55e' }}>Import Complete</p>
              <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                {importResult.imported} tasks imported, {importResult.skipped} duplicates skipped
              </p>
            </div>
          </div>
          <button
            onClick={() => setImportResult(null)}
            className="p-2 rounded-lg transition hover:bg-white/10"
            style={{ color: 'rgba(148, 163, 184, 0.6)' }}
          >
            <i className="fa-solid fa-times"></i>
          </button>
        </div>
      )}

      {/* Import Error Banner */}
      {importError && (
        <div
          className="rounded-xl p-4 flex items-center justify-between"
          style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
        >
          <div className="flex items-center gap-3">
            <i className="fa-solid fa-exclamation-circle text-xl" style={{ color: '#ef4444' }}></i>
            <div>
              <p className="font-medium" style={{ color: '#ef4444' }}>Import Failed</p>
              <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>{importError}</p>
            </div>
          </div>
          <button
            onClick={() => setImportError(null)}
            className="p-2 rounded-lg transition hover:bg-white/10"
            style={{ color: 'rgba(148, 163, 184, 0.6)' }}
          >
            <i className="fa-solid fa-times"></i>
          </button>
        </div>
      )}

      {/* Delete Result Banner */}
      {deleteResult && (
        <div
          className="rounded-xl p-4 flex items-center justify-between"
          style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}
        >
          <div className="flex items-center gap-3">
            <i className="fa-solid fa-check-circle text-xl" style={{ color: '#22c55e' }}></i>
            <div>
              <p className="font-medium" style={{ color: '#22c55e' }}>Delete Complete</p>
              <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                {deleteResult.deleted} task(s) deleted
              </p>
            </div>
          </div>
          <button
            onClick={() => setDeleteResult(null)}
            className="p-2 rounded-lg transition hover:bg-white/10"
            style={{ color: 'rgba(148, 163, 184, 0.6)' }}
          >
            <i className="fa-solid fa-times"></i>
          </button>
        </div>
      )}

      {/* Bulk Action Bar - shown when tasks are selected */}
      {!presentationMode && (selectedTasks.size > 0 || tasks.length > 0) && (
        <div
          className="rounded-xl p-4 flex items-center justify-between"
          style={glassPanel}
        >
          <div className="flex items-center gap-4">
            <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
              {selectedTasks.size > 0 ? (
                <><strong style={{ color: '#e2e8f0' }}>{selectedTasks.size}</strong> of {tasks.length} selected</>
              ) : (
                <>{tasks.length} tasks total</>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {selectedTasks.size > 0 && (
              <button
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 disabled:opacity-50"
                style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444' }}
              >
                {isDeleting ? (
                  <i className="fa-solid fa-spinner fa-spin"></i>
                ) : (
                  <i className="fa-solid fa-trash"></i>
                )}
                Delete Selected ({selectedTasks.size})
              </button>
            )}
            <button
              onClick={handleDeleteAll}
              disabled={isDeleting || tasks.length === 0}
              className="px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 disabled:opacity-50"
              style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#f87171' }}
            >
              {isDeleting ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : (
                <i className="fa-solid fa-trash-can"></i>
              )}
              Delete All ({tasks.length})
            </button>
          </div>
        </div>
      )}

      {/* Task List */}
      <div className="rounded-xl overflow-hidden" style={glassPanel}>
        {tasksLoading ? (
          <div className="p-8 text-center" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            <i className="fa-solid fa-spinner fa-spin text-2xl mb-2"></i>
            <p>Loading tasks...</p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="p-8 text-center" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            <i className="fa-solid fa-clipboard-list text-4xl mb-3" style={{ color: 'rgba(148, 163, 184, 0.3)' }}></i>
            <p className="text-lg font-medium mb-1">No tasks in backlog</p>
            <p className="text-sm">Click "Import All Findings" above to populate the backlog from audit findings.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ background: 'rgba(30, 41, 59, 0.5)', borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
                  {!presentationMode && (
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedTasks.size === tasks.length && tasks.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded cursor-pointer"
                      style={{ accentColor: '#60a5fa' }}
                    />
                  </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Risk</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Finding</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Impact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Estimate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Status</th>
                  {!presentationMode && (
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'rgba(100, 180, 255, 0.1)' }}>
                {tasks.map((task) => (
                  <React.Fragment key={task.id}>
                  <tr
                    className="hover:bg-opacity-50 transition cursor-pointer"
                    style={{ background: selectedTasks.has(task.id) ? 'rgba(96, 165, 250, 0.15)' : selectedTask?.id === task.id ? 'rgba(96, 165, 250, 0.1)' : searchTerm && task.id.toLowerCase().includes(searchTerm.toLowerCase()) ? 'rgba(251, 191, 36, 0.1)' : 'transparent' }}
                    onClick={() => setSelectedTask(selectedTask?.id === task.id ? null : task)}
                  >
                    {!presentationMode && (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedTasks.has(task.id)}
                        onChange={(e) => {
                          e.stopPropagation()
                          handleSelectTask(task.id)
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded cursor-pointer"
                        style={{ accentColor: '#60a5fa' }}
                      />
                    </td>
                    )}
                    <td className="px-4 py-3">
                      <select
                        value={task.priority}
                        onChange={(e) => {
                          e.stopPropagation()
                          handlePriorityChange(task.id, e.target.value as TaskPriority)
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="px-2 py-1 rounded text-xs font-bold"
                        style={{
                          background: `${priorityColors[task.priority]}20`,
                          border: `1px solid ${priorityColors[task.priority]}50`,
                          color: priorityColors[task.priority],
                        }}
                      >
                        <option value="P1">P1</option>
                        <option value="P2">P2</option>
                        <option value="P3">P3</option>
                        <option value="P4">P4</option>
                        <option value="P5">P5</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-1 rounded text-xs font-bold"
                        style={{
                          background: task.risk_score && task.risk_score >= 70 ? 'rgba(220, 38, 38, 0.2)' : task.risk_score && task.risk_score >= 50 ? 'rgba(251, 191, 36, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                          color: task.risk_score && task.risk_score >= 70 ? '#dc2626' : task.risk_score && task.risk_score >= 50 ? '#fbbf24' : '#22c55e',
                        }}
                      >
                        {task.risk_score || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-md">
                        <div className="text-sm font-medium truncate" style={{ color: '#e2e8f0' }}>
                          {task.finding_title}
                        </div>
                        {task.resource_name && (
                          <div className="text-xs truncate" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                            {task.resource_name}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs capitalize" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                        {task.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-1 rounded text-xs font-medium capitalize"
                        style={{
                          background: `${impactColors[task.impact]}20`,
                          color: impactColors[task.impact],
                        }}
                      >
                        {task.impact}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                        {task.time_estimate}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={task.status}
                        onChange={(e) => {
                          e.stopPropagation()
                          handleStatusChange(task.id, e.target.value as TaskStatus)
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{
                          background: `${statusColors[task.status]}20`,
                          border: `1px solid ${statusColors[task.status]}50`,
                          color: statusColors[task.status],
                        }}
                      >
                        {Object.entries(statusLabels).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </td>
                    {!presentationMode && (
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (confirm('Delete this task?')) {
                            deleteTaskMutation.mutate(task.id)
                          }
                        }}
                        className="p-1.5 rounded hover:bg-red-500/20 transition"
                        style={{ color: 'rgba(248, 113, 113, 0.7)' }}
                      >
                        <i className="fa-solid fa-trash text-xs"></i>
                      </button>
                    </td>
                    )}
                  </tr>
                  {/* Expanded detail row */}
                  {selectedTask?.id === task.id && task.notes && (
                    <tr>
                      <td colSpan={10} className="px-4 py-3" style={{ background: 'rgba(30, 41, 59, 0.4)' }}>
                        <div className="pl-8 text-sm" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>
                          <div className="whitespace-pre-wrap font-sans text-xs leading-relaxed" style={{ color: 'rgba(148, 163, 184, 0.85)' }}>
                            {task.notes.replace(/\*\*/g, '').split('\n').map((line, i) => (
                              <div key={i} className={line.startsWith('-') ? 'pl-2' : 'font-medium mt-2 first:mt-0'} style={{ color: line.startsWith('-') ? 'rgba(148, 163, 184, 0.7)' : '#e2e8f0' }}>
                                {line}
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Task count */}
      <div className="text-center text-sm" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
        Showing {tasks.length} tasks
      </div>
    </div>
  )
}

// Add to Backlog Button Component (to be used in findings cards)
export function AddToBacklogButton({
  projectId,
  auditId,
  finding,
  category,
  severity,
  resourceName,
  subscription,
  recommendation,
  sourceFile,
  onSuccess,
}: {
  projectId: string
  auditId: string
  finding: string
  category: string
  severity: string
  resourceName?: string
  subscription?: string
  recommendation?: string
  sourceFile?: string
  onSuccess?: () => void
}) {
  const queryClient = useQueryClient()
  const [isAdding, setIsAdding] = useState(false)

  const createTaskMutation = useMutation({
    mutationFn: async (task: RemediationTaskCreate) => {
      const response = await createRemediationTask(task)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['remediation-tasks'] })
      queryClient.invalidateQueries({ queryKey: ['remediation-task-stats'] })
      onSuccess?.()
    },
  })

  const handleAddToBacklog = async () => {
    setIsAdding(true)

    // Map severity to impact and priority
    const impactMap: Record<string, TaskImpact> = {
      Critical: 'critical',
      High: 'high',
      Medium: 'medium',
      Low: 'low',
      Info: 'info',
    }
    const priorityMap: Record<string, TaskPriority> = {
      Critical: 'P1',
      High: 'P2',
      Medium: 'P3',
      Low: 'P4',
      Info: 'P5',
    }

    // Estimate time and complexity based on finding characteristics
    const estimateTime = (title: string, cat: string, sev: string): { time: TimeEstimate; complexity: TaskComplexity } => {
      const titleLower = title.toLowerCase()
      const catLower = cat.toLowerCase()
      const sevLower = sev.toLowerCase()

      // Quick fixes - settings that can be enabled/disabled
      const quickKeywords = [
        'enable', 'disable', 'turn on', 'turn off', 'not enabled', 'not configured',
        'missing tag', 'diagnostic', 'https', 'tls', 'encryption', 'backup',
        'mfa', 'defender', 'monitoring', 'logging', 'public access', 'firewall rule'
      ]

      // Medium complexity - requires planning
      const mediumKeywords = [
        'migrate', 'upgrade', 'deprecated', 'key rotation', 'certificate',
        'access review', 'permission', 'rbac', 'network', 'vnet', 'policy',
        'identity', 'managed identity', 'alert', 'cost', 'rightsizing', 'redundancy'
      ]

      // Complex tasks - architectural changes
      const complexKeywords = [
        'architecture', 'redesign', 'disaster recovery', 'high availability',
        'hub and spoke', 'landing zone', 'zero trust', 'automation', 'terraform', 'devops'
      ]

      if (complexKeywords.some(kw => titleLower.includes(kw))) {
        return { time: '1 week', complexity: 'complex' }
      }
      if (quickKeywords.some(kw => titleLower.includes(kw))) {
        if (['enable', 'disable', 'not enabled', 'not configured'].some(x => titleLower.includes(x))) {
          return { time: '1 hour', complexity: 'trivial' }
        }
        return { time: '2-4 hours', complexity: 'simple' }
      }
      if (mediumKeywords.some(kw => titleLower.includes(kw))) {
        return { time: '4-8 hours', complexity: 'moderate' }
      }

      // Category-based defaults
      if (catLower.includes('cost')) {
        return { time: '2-4 hours', complexity: 'simple' }
      }
      if (catLower.includes('security')) {
        return sevLower === 'critical' || sevLower === 'high'
          ? { time: '2-4 hours', complexity: 'simple' }
          : { time: '4-8 hours', complexity: 'moderate' }
      }

      // Default based on severity
      if (sevLower === 'info') return { time: '1 hour', complexity: 'trivial' }
      if (sevLower === 'low') return { time: '2-4 hours', complexity: 'simple' }
      return { time: '4-8 hours', complexity: 'moderate' }
    }

    const { time, complexity } = estimateTime(finding, category, severity)

    try {
      await createTaskMutation.mutateAsync({
        project_id: projectId,
        audit_id: auditId,
        finding_title: finding.length > 100 ? finding.substring(0, 97) + '...' : finding,
        finding_description: finding,
        category: category.toLowerCase(),
        resource_name: resourceName,
        subscription: subscription,
        source_file: sourceFile,
        impact: impactMap[severity] || 'medium',
        priority: priorityMap[severity] || 'P3',
        complexity: complexity,
        time_estimate: time,
        recommendation: recommendation,
      })
    } catch (error) {
      console.error('Failed to add to backlog:', error)
    }

    setIsAdding(false)
  }

  return (
    <button
      onClick={handleAddToBacklog}
      disabled={isAdding || createTaskMutation.isPending}
      className="px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-2 disabled:opacity-50"
      style={{
        background: createTaskMutation.isSuccess ? 'rgba(34, 197, 94, 0.2)' : 'rgba(167, 139, 250, 0.2)',
        border: `1px solid ${createTaskMutation.isSuccess ? 'rgba(34, 197, 94, 0.3)' : 'rgba(167, 139, 250, 0.3)'}`,
        color: createTaskMutation.isSuccess ? '#22c55e' : '#a78bfa',
      }}
    >
      {isAdding ? (
        <>
          <i className="fa-solid fa-spinner fa-spin"></i>
          Adding...
        </>
      ) : createTaskMutation.isSuccess ? (
        <>
          <i className="fa-solid fa-check"></i>
          Added
        </>
      ) : (
        <>
          <i className="fa-solid fa-plus"></i>
          Add to Backlog
        </>
      )}
    </button>
  )
}
